import { Box, Menu, MenuItem, Tooltip } from "@mui/material";
import * as React from "react";
import type { IEnvelopeData } from "../types/types";

// ✅ CORREÇÃO: Removendo 'edit' e 'details' e adicionando as abas válidas do modal
export type EnvelopeMenuAction = "sendToSign" | "cancel" | "dados" | "signatarios" | "documentos";

interface Props {
	anchorEl: HTMLElement | null;
	open: boolean;
	onClose: () => void;
	selectedRow: IEnvelopeData | null;
	onSelect: (action: EnvelopeMenuAction, row: IEnvelopeData) => void;
}

type EnvelopeStatusNorm = "draft" | "running" | "canceled" | "closed" | "other";

const norm = (s?: string | null) => (s ?? "").trim().toLowerCase();

function normalizeEnvelopeStatus(raw?: string | null): EnvelopeStatusNorm {
	const n = norm(raw);
	if (n === "draft" || n === "rascunho") return "draft";
	if (n === "running" || n === "em_progresso" || n === "em progresso" || n === "em_andamento") return "running";
	if (n === "canceled" || n === "cancelado" || n === "cancelada") return "canceled";
	if (n === "closed" || n === "assinado" || n === "finalizado" || n === "concluido" || n === "concluído") return "closed";
	return "other";
}

const hasSigners = (row: IEnvelopeData) => (row.signatories ?? []).some((p) => ["signer", "witness"].includes((p.type ?? "").toLowerCase()));
const hasDocuments = (row: IEnvelopeData) => (row.documents ?? []).length > 0;

function computePermissions(row: IEnvelopeData | null) {
	if (!row) {
		const reason = "Selecione um envelope.";
		return {
			canSend: false,
			reasonSend: reason,
			canCancel: false,
			reasonCancel: reason,
			canEdit: false,
			reasonEdit: reason,
		};
	}

	const status = normalizeEnvelopeStatus(row.status);
	const hasDocs = hasDocuments(row);
	const hasSigner = hasSigners(row);

	const isReadyToSign = status === "draft" && hasDocs && hasSigner;
	let reasonSend = "";
	if (status !== "draft")
		reasonSend = `O envelope está em status de "${status === "running" ? "em andamento" : status}" e não pode ser enviado para assinatura.`;
	else if (!hasDocs) reasonSend = "O envelope não possui documentos.";
	else if (!hasSigner) reasonSend = "O envelope não possui assinantes ou testemunhas.";
	const canSend = isReadyToSign;

	const canCancel = status !== "canceled" && status !== "closed";
	const reasonCancel = canCancel ? "" : `O envelope já está em status de "${status}".`;

	const canEdit = status !== "canceled" && status !== "closed" && status !== "running";
	const reasonEdit = canEdit ? "" : `A edição está bloqueada para envelopes em status de "${status}".`;

	return { canSend, reasonSend, canCancel, reasonCancel, canEdit, reasonEdit };
}

const TooltipIfDisabled: React.FC<{ title: string; disabled: boolean; children: React.ReactNode }> = ({ title, disabled, children }) =>
	disabled ? (
		<Tooltip title={title}>
			<span style={{ display: "block" }}>{children}</span>
		</Tooltip>
	) : (
		<>{children}</>
	);

export const EnvelopeMenu: React.FC<Props> = ({ anchorEl, open, onClose, selectedRow, onSelect }) => {
	const { canSend, reasonSend, canCancel, reasonCancel, canEdit, reasonEdit } = computePermissions(selectedRow);

	const handle = (action: EnvelopeMenuAction) => {
		if (selectedRow) onSelect(action, selectedRow);
		onClose();
	};

	

	return (
		<Menu anchorEl={anchorEl} open={open} onClose={onClose}>
			<Box>
				<MenuItem onClick={() => handle("dados")}>Detalhes do envelope</MenuItem>

				<TooltipIfDisabled title={reasonEdit} disabled={!canEdit}>
					{/* ✅ CORREÇÃO: "Editar envelope" agora abre a aba "dados" */}
					<MenuItem disabled={!canEdit} onClick={() => handle("dados")}>
						Editar envelope
					</MenuItem>
				</TooltipIfDisabled>

				<TooltipIfDisabled title={reasonSend} disabled={!canSend}>
					<MenuItem disabled={!canSend} onClick={() => handle("sendToSign")}>
						Enviar para assinar
					</MenuItem>
				</TooltipIfDisabled>

				<TooltipIfDisabled title={reasonCancel} disabled={!canCancel}>
					<MenuItem disabled={!canCancel} onClick={() => handle("cancel")}>
						Cancelar
					</MenuItem>
				</TooltipIfDisabled>
			</Box>
		</Menu>
	);
};
